import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, LucideIcon } from 'lucide-react';

interface Step {
  id: number;
  name: string;
  icon: LucideIcon;
  titleKey: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep }) => {
  const { t } = useTranslation();
  return (
    <div className="relative">
      {/* Progress bar background */}
      <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700" />

      {/* Progress bar filled */}
      <div
        className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-primary-500 to-purple-500 transition-all duration-500 ease-out"
        style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
      />

      {/* Steps */}
      <div className="relative flex justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const Icon = step.icon;

          return (
            <div
              key={step.id}
              className="flex flex-col items-center"
            >
              {/* Circle */}
              <div
                className={`
                  relative z-10 w-10 h-10 rounded-full flex items-center justify-center
                  transition-all duration-300 ease-out
                  ${isCompleted
                    ? 'bg-gradient-to-br from-primary-500 to-purple-500 text-white shadow-lg shadow-primary-500/30'
                    : isCurrent
                      ? 'bg-white dark:bg-gray-800 border-2 border-primary-500 text-primary-600 dark:text-primary-400 shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <Icon className="h-5 w-5" />
                )}

                {/* Pulse animation for current step */}
                {isCurrent && (
                  <span className="absolute -inset-1 rounded-full bg-primary-400 animate-ping opacity-30" />
                )}
              </div>

              {/* Label */}
              <span
                className={`
                  mt-2 text-xs font-medium hidden sm:block
                  ${isCurrent
                    ? 'text-primary-600 dark:text-primary-400'
                    : isCompleted
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                  }
                `}
              >
                {t(step.titleKey)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepIndicator;
